"use client";

import Image from "next/image";
import { Cormorant_Garamond } from "next/font/google";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { ChevronDown, Gem, Wrench } from "lucide-react";
import Section from "./section";
import { useIsMobile } from "@/hooks/use-mobile";
import type { AccordionContent, LocaleCode } from "@/types/sections";
import { pickLocalizedList, pickLocalizedString } from "@/lib/locale";
import { resolveIcon } from "@/lib/icon-map";
import { sanitizeImageUrl, fallbackImage } from "@/lib/image-safety";
import styles from "./interactive-image-accordion.module.css";

const titleFont = Cormorant_Garamond({
  subsets: ["latin"],
  weight: "400",
  display: "swap",
});

type Props = {
  content: AccordionContent;
  locale: LocaleCode;
};

type AccordionItemData = {
  id: string;
  title: string;
  imageUrl: string;
  color: string;
  iconKey?: string;
  paragraphs: string[];
};

function CigarIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="2 5 44 36" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <g transform="rotate(-20 24 30)">
        <rect x="4" y="24" width="38" height="11" rx="5.5" />
        <path d="M13 24v11M18 24v11M35 24v11" />
      </g>
      <path d="M35 19c5-5-5-5-1-10M38 15c3-4-3-4-2-7" />
    </svg>
  );
}

function ServiceCopy({ paragraphs }: { paragraphs: string[] }) {
  const blocks: React.ReactNode[] = [];
  for (let index = 0; index < paragraphs.length; index++) {
    const paragraph = paragraphs[index];
    if (paragraph.startsWith("•")) {
      const bullets: string[] = [];
      const start = index;
      while (index < paragraphs.length && paragraphs[index].startsWith("•")) {
        bullets.push(paragraphs[index].replace(/^•\s*/, ""));
        index++;
      }
      index--;
      blocks.push(<ul key={start}>{bullets.map((bullet, i) => <li key={i}>{bullet}</li>)}</ul>);
    } else {
      blocks.push(<p key={index}>{paragraph}</p>);
    }
  }
  return <>{blocks}</>;
}

function AccordionItem({ item, isActive, onToggle, panelId }: {
  item: AccordionItemData;
  isActive: boolean;
  onToggle: () => void;
  panelId: string;
}) {
  const [imgErrored, setImgErrored] = useState(false);
  const Icon = item.iconKey === "Cigar" ? CigarIcon : resolveIcon(item.iconKey, Gem);
  const hasImage = Boolean(item.imageUrl);

  return (
    <article
      className={`${styles.item} ${isActive ? styles.active : ""} ${hasImage ? styles.withImage : ""}`}
      style={{ "--service-color": item.color } as React.CSSProperties}
      data-service-id={item.id}
    >
      {hasImage && isActive && (
        <div className={styles.background} aria-hidden="true">
          <Image
            src={imgErrored ? fallbackImage() : sanitizeImageUrl(item.imageUrl)}
            alt=""
            fill
            className="object-cover"
            sizes="(min-width: 1024px) 60vw, 100vw"
            quality={80}
            onError={() => setImgErrored(true)}
          />
        </div>
      )}
      <h3 className={styles.heading}>
        <button
          type="button"
          id={`${panelId}-trigger`}
          className={styles.trigger}
          aria-expanded={isActive}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span className={`${styles.title} ${titleFont.className}`}>{item.title}</span>
          <Icon
            className={`${styles.icon} ${item.iconKey === "Cigar" ? styles.cigarIcon : ""}`}
            aria-hidden="true"
            strokeWidth={item.iconKey === "Cigar" ? 1.8 : 1.4}
          />
          <ChevronDown className={styles.chevron} aria-hidden="true" size={20} />
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={`${panelId}-trigger`}
        hidden={!isActive}
        tabIndex={isActive ? 0 : -1}
        className={styles.content}
      >
        <ServiceCopy paragraphs={item.paragraphs} />
      </div>
    </article>
  );
}

export function LandingAccordionItem({ content, locale }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pointerPaused, setPointerPaused] = useState(false);
  const [focusPaused, setFocusPaused] = useState(false);
  const stripRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stripRef, { amount: 0.25 });
  const reducedMotion = useReducedMotion();
  const itemIds = useMemo(() => (content.items ?? []).map((item) => item.id), [content.items]);
  const instanceId = useId();
  const isMobile = useIsMobile();
  const SectionIcon = resolveIcon(content.iconKey, Wrench);
  const items = (content.items ?? []).map((item) => ({
    id: item.id,
    title: pickLocalizedString(item.title, locale),
    imageUrl: item.imageUrl,
    color: item.color,
    iconKey: item.iconKey,
    paragraphs: pickLocalizedList(item.paragraphs, locale),
  }));
  const hasActiveItem = items.some((item) => item.id === activeId);

  useEffect(() => {
    if (!inView) setPointerPaused(false);
  }, [inView]);

  useEffect(() => {
    if (!inView || reducedMotion || pointerPaused || focusPaused || itemIds.length < 2) return;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      setActiveId((current) => {
        const index = current ? itemIds.indexOf(current) : -1;
        return itemIds[(index + 1) % itemIds.length];
      });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [inView, reducedMotion, pointerPaused, focusPaused, itemIds]);

  return (
    <Section
      id="services"
      title={pickLocalizedString(content.title, locale, locale === "fr" ? "Nos Services" : "Our Services")}
      eyebrow={pickLocalizedString(content.eyebrow, locale)}
      subtitle={pickLocalizedString(content.subtitle, locale)}
      variant={isMobile ? "left" : "center"}
      Icon={SectionIcon}
    >
      {items.length ? (
        <div
          ref={stripRef}
          className={`${styles.strip} ${hasActiveItem ? styles.expanded : ""}`}
          onPointerEnter={() => setPointerPaused(true)}
          onPointerLeave={(event) => {
            if (event.pointerType !== "touch") setPointerPaused(false);
          }}
          onFocusCapture={() => setFocusPaused(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setFocusPaused(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape" && activeId) {
              const trigger = event.currentTarget.querySelector<HTMLButtonElement>('button[aria-expanded="true"]');
              trigger?.focus();
              setActiveId(null);
            }
          }}
        >
          {items.map((item, index) => (
            <AccordionItem
              key={item.id}
              item={item}
              isActive={activeId === item.id}
              onToggle={() => setActiveId((current) => current === item.id ? null : item.id)}
              panelId={`${instanceId}-service-${index}`}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {locale === "fr" ? "Nos services seront bientôt disponibles." : "Our services will be available soon."}
        </p>
      )}
    </Section>
  );
}
